/**
 * Per-item access reported by the API.
 *
 * Access is resolved per record, not per family: a permission granted on the record itself, on the
 * member it is about, or on the record because you authored it, is only visible in these fields. A
 * family role cannot see any of it, so gating on the family role alone hides actions from people
 * who are entitled to them.
 */

/** The access fields the API resolves for one record for the calling user. */
export type ItemAccessFields = {
  effectiveRole?: string | null;
  canModify?: boolean;
  canDelete?: boolean;
};

/**
 * Whether the signed-in user may edit one record.
 *
 * `fallback` is only consulted when the API did not report access, which happens when the record
 * comes from an API build older than the client. When the API did report access its answer wins,
 * even if it is more generous than the caller's family role.
 */
export function canModifyItem(item: ItemAccessFields | null | undefined, fallback: boolean): boolean {
  if (!item) return false;
  if (item.effectiveRole == null) return fallback;
  return item.canModify === true;
}

/**
 * Whether the signed-in user may delete one record. Falls back to `canModify` when the API reports
 * access but not a separate delete flag.
 */
export function canDeleteItem(item: ItemAccessFields | null | undefined, fallback: boolean): boolean {
  if (!item) return false;
  if (item.effectiveRole == null) return fallback;
  return item.canDelete ?? item.canModify === true;
}
